// Client-side resume text extraction. PDF via pdfjs-dist, DOCX via mammoth —
// both loaded through dynamic import so the (fairly large) parsing libraries
// only enter the bundle when a job seeker actually uploads a resume.
//
// pdfjs-dist is pinned at ^5.4.54; that version ships its worker as
// `build/pdf.worker.min.mjs`. We import it with Vite's `?url` suffix so the
// bundler emits it as a hashed asset and hands back its real URL — this
// keeps the worker in lockstep with whatever pdfjs-dist version is
// installed, rather than relying on a hand-copied file under `public/`.

export function pickExtractor(mime: string, name: string): 'pdf' | 'docx' {
  const lower = name.toLowerCase();
  if (mime === 'application/pdf' || lower.endsWith('.pdf')) {
    return 'pdf';
  }
  if (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    lower.endsWith('.docx')
  ) {
    return 'docx';
  }
  throw new Error('Only PDF and DOCX resumes are supported.');
}

export async function extractText(file: File): Promise<string> {
  const kind = pickExtractor(file.type, file.name);
  const buf = await file.arrayBuffer();

  if (kind === 'docx') {
    const mammoth = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer: buf });
    return result.value;
  }

  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const doc = await pdfjs.getDocument({ data: buf }).promise;
  let out = '';
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    out += content.items.map((item) => ('str' in item ? item.str : '')).join(' ') + '\n';
  }
  return out.trim();
}
